import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { log, error as logError } from '../utils/DebugLogger';
import { validate } from '../contracts/validate';
import { useDateFormatter } from './useDateFormatter';
import '../contracts/Combos_EstornarInput';
import '../contracts/Combos_AjustarInput';
import '../contracts/Combos_CancelarCompraInput';
import '../contracts/Orders_VincularComboInput';
import '../contracts/ComboRelatorios_ClienteResponse';
import '../contracts/Combos_EstornarResponse';
import '../contracts/Combos_AjustarResponse';
import '../contracts/Combos_CancelarCompraResponse';
import '../contracts/Orders_VincularComboResponse';
import '../contracts/Orders_DesvincularComboResponse';
import type { ComboRelatorios_CompraRow } from '../contracts/ComboRelatorios_CompraRow';
import type { ComboRelatorios_MovimentoRow } from '../contracts/ComboRelatorios_MovimentoRow';
import type { Combos_EstornarInput } from '../contracts/Combos_EstornarInput';
import type { Combos_AjustarInput } from '../contracts/Combos_AjustarInput';
import type { Combos_CancelarCompraInput } from '../contracts/Combos_CancelarCompraInput';
import type { Orders_VincularComboInput } from '../contracts/Orders_VincularComboInput';

/**
 * As compras de combo de um cliente (NS 2026-0090): consulta, uso numa OS
 * (vincular/desvincular) e as correções do razão (estorno, ajuste, cancelamento).
 *
 * Fronteira de dados: o wire fala CENTAVOS e ISO 8601 UTC; daqui para a tela é
 * unidade da moeda e `Date`. O saldo é sempre o do backend — nada é somado aqui.
 */
export type MovimentoCombo = Omit<ComboRelatorios_MovimentoRow, 'created_at' | 'preco_matriz'> & {
  created_at: Date | null;
  preco_matriz: number | null;
};

export type CompraCombo = Omit<
  ComboRelatorios_CompraRow,
  'created_at' | 'expires_at' | 'cancelled_at' | 'movimentos' | 'preco_total' | 'venda_total' | 'venda_recebido'
> & {
  created_at: Date | null;
  expires_at: Date | null;
  cancelled_at: Date | null;
  preco_total: number;
  venda_total: number;
  venda_recebido: number;
  movimentos: MovimentoCombo[];
};

export interface AlvoDoCombo {
  serviceId: number;
  priorityId: number;
  franchiseId: number;
  /** Na edição: a compra que já cobre o item. Fica na lista mesmo sem saldo ou vencida, porque o par dela já está reservado aqui. */
  compraDoItem?: number | null;
}

/**
 * As compras que podem cobrir um par: mesmo serviço + prioridade, mesma unidade
 * da OS, ativa, com saldo e não vencida.
 *
 * Serviço e prioridade são os COPIADOS na venda, nunca os do catálogo atual: é o
 * que o backend confere, e mudar o combo depois não muda o que a compra cobre.
 * É filtro de tela — a recusa com a frase do motivo continua sendo do backend.
 */
export function comprasCompativeis(compras: CompraCombo[], alvo: AlvoDoCombo): CompraCombo[] {
  return compras.filter((c) =>
    c.franquia_id === alvo.franchiseId
    && c.service_id === alvo.serviceId
    && c.priority_id === alvo.priorityId
    && (c.compra_id === alvo.compraDoItem || (c.status === 'ativa' && c.disponivel > 0 && !c.vencida)));
}

export function useClienteCombos() {
  const { fromApiDateTimeFormat } = useDateFormatter();
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  const paraDate = (valor?: string | null): Date | null => (valor ? fromApiDateTimeFormat(valor) : null);

  const movimentoParaTela = (m: ComboRelatorios_MovimentoRow): MovimentoCombo => ({
    ...m,
    created_at: paraDate(m.created_at),
    preco_matriz: m.preco_matriz == null ? null : m.preco_matriz / 100,
  });

  const compraParaTela = (c: ComboRelatorios_CompraRow): CompraCombo => ({
    ...c,
    created_at: paraDate(c.created_at),
    expires_at: paraDate(c.expires_at),
    cancelled_at: paraDate(c.cancelled_at),
    preco_total: c.preco_total / 100,
    venda_total: c.venda_total / 100,
    venda_recebido: c.venda_recebido / 100,
    movimentos: (c.movimentos ?? []).map(movimentoParaTela),
  });

  /** Roda uma chamada com o estado do composable; a recusa do backend sobe como Error com a frase dele. */
  const executar = async <T>(metodo: string, fallback: string, chamada: () => Promise<{ success?: boolean; error?: string | null; dados?: T }>): Promise<T> => {
    isLoading.value = true;
    error.value = null;
    try {
      const response = await chamada();
      if (response.success) {
        return response.dados as T;
      }
      throw new Error(response.error || fallback);
    } catch (err) {
      logError('useClienteCombos', metodo, 'Error', err);
      error.value = err instanceof Error ? err.message : String(err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const fetchCompras = async (clienteId: number): Promise<CompraCombo[]> => {
    log('useClienteCombos', 'fetchCompras', 'Fetching purchases', { clienteId });
    const dados = await executar<{ compras?: ComboRelatorios_CompraRow[] }>('fetchCompras', 'Erro ao buscar os combos do cliente', () =>
      httpService.get(`/index.php/so/combo_relatorios/cliente/${clienteId}`, { responseContract: 'ComboRelatorios_ClienteResponse' }));
    return (dados?.compras ?? []).map(compraParaTela);
  };

  const estornar = (data: Combos_EstornarInput) => executar('estornar', 'Erro ao estornar o consumo', () => {
    validate('Combos_EstornarInput', data);
    return httpService.post('/index.php/so/combos/estornar', data, { responseContract: 'Combos_EstornarResponse' });
  });

  const ajustar = (data: Combos_AjustarInput) => executar('ajustar', 'Erro ao ajustar o saldo do combo', () => {
    validate('Combos_AjustarInput', data);
    return httpService.post('/index.php/so/combos/ajustar', data, { responseContract: 'Combos_AjustarResponse' });
  });

  const cancelarCompra = (data: Combos_CancelarCompraInput) => executar('cancelarCompra', 'Erro ao cancelar a compra do combo', () => {
    validate('Combos_CancelarCompraInput', data);
    return httpService.post('/index.php/so/combos/cancelar_compra', data, { responseContract: 'Combos_CancelarCompraResponse' });
  });

  const vincular = (itemId: number, data: Orders_VincularComboInput) => executar('vincular', 'Erro ao usar o combo neste item', () => {
    validate('Orders_VincularComboInput', data);
    return httpService.post(`/index.php/so/orders/vincular_combo/${itemId}`, data, { responseContract: 'Orders_VincularComboResponse' });
  });

  const desvincular = (itemId: number) => executar('desvincular', 'Erro ao desvincular o combo', () =>
    httpService.post(`/index.php/so/orders/desvincular_combo/${itemId}`, {}, { responseContract: 'Orders_DesvincularComboResponse' }));

  return {
    isLoading,
    error,
    movimentoParaTela,
    fetchCompras,
    estornar,
    ajustar,
    cancelarCompra,
    vincular,
    desvincular,
  };
}
